/* eslint-disable no-unused-vars */

import React, { FC, Fragment, memo, useMemo, useState } from 'react';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles
} from '@material-ui/core';
import clsx from 'clsx';
import InfiniteScroll from 'react-infinite-scroll-component';
import { colors } from 'src/constants';
import {
  IncomingProductPriceListData,
  PoweredBuildsPriceListData,
  PriceListColumn,
  ProductPriceListData
} from 'src/types';
import { formatCurrency, getColorGeneric, objectToUrlSlug } from 'src/utils';
import { Name } from './TableCell/Name';
import { TypographyPriceList } from './TypographyPriceList';
import { usePermissions } from 'src/hooks';
import { slices, useAppSelector } from 'src/redux';
import { cloneDeep, sortBy } from 'lodash';
import { priceListColumns } from '../constants';
import { LoaderBar } from 'src/components';
import { ProductPriceModal } from './ProductPriceModal';
import { BranchStockList } from './BranchStockList';
import { StocksQuantityList } from './StocksQuantityList';
import { InstallmentList } from './InstallmentList';
import { uniqBy } from 'lodash';
import { useNavigate } from 'react-router';
import { usePriceList } from 'src/hooks/priceList';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ClearIcon from '@material-ui/icons/Clear';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(() => ({
  table: {
    border: '2px solid black',
    borderColor: colors.primary,
    maxWidth: 2000
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: colors.grey[200]
    },
    '&:hover': {
      backgroundColor: colors.pink[50]
    }
  },
  tableHeader: {
    backgroundColor: colors.orange[200],
    padding: '0.5rem',
    margin: 0
  },
  tableCell: {
    padding: '0.5rem',
    textAlign: 'center',
    borderRight: '0.5px solid black',
    borderColor: colors.primary,
    cursor: 'pointer'
  },
  compactCell: {
    padding: '0.3rem'
  },
  multiCellParent: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: 0
  },
  boxCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: '1px solid black',
    padding: '0.5rem',
    '&:last-child': {
      borderBottom: 'none'
    }
  }
}));

interface MainPriceListTableProps {
  isLoading: boolean;
  branchIds?: number[];
  priceList: (PoweredBuildsPriceListData &
    ProductPriceListData &
    IncomingProductPriceListData)[];
  isPoweredBuildsTable?: boolean;
  isIncomingPriceListTable?: boolean;
  onUpdatePriceList: (data: ProductPriceListData) => void;
  onLoadMoreItems: () => void;
}

const { selectors: appSelectors } = slices.product;

const component: FC<MainPriceListTableProps> = ({
  isLoading,
  branchIds,
  priceList,
  isPoweredBuildsTable = false,
  isIncomingPriceListTable = false,

  onUpdatePriceList,
  onLoadMoreItems
}) => {
  const navigate = useNavigate();
  const selectedPriceListColumns = useAppSelector(
    appSelectors.selectPriceListColumns
  );

  const { meta, params } = usePriceList();

  const classes = useStyles();
  const {
    canSeePriceListDealersPrice,
    canSeePriceListGrossPrice,
    canSeePriceListMarginPercentage,
    canUpdateRetailPrice,
    userPermissionsStringArray,
    canViewSalesPriceList
  } = usePermissions();

  const [showProductModal, setShowProductModal] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<
    ProductPriceListData | undefined
  >();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  const nonSortableCodes = ['branch', 'quantity'];

  const loadMore = useMemo(() => meta.last_page != meta.current_page, [meta]);

  const internalColumnsWithPermissions = useMemo(() => {
    const clonedColumns = cloneDeep(priceListColumns || []);
    let columnsWillAppear: PriceListColumn[] = clonedColumns;
    const removeColumn = ['TS QTY', 'DP', 'GROSS', 'MARGIN %'];

    columnsWillAppear = columnsWillAppear?.filter((x) => {
      if (selectedPriceListColumns[x.code]) {
        if (x?.permissions && x?.permissions?.length > 0) {
          return x?.permissions?.some((r) =>
            userPermissionsStringArray?.includes(r)
          );
        }

        return true;
      }
      return false;
    });

    const toRemoveColumns = (labels: string[]) => {
      labels.forEach((label) => {
        const index = columnsWillAppear.findIndex((i) => i.label === label);
        if (index !== -1) {
          columnsWillAppear.splice(index, 1);
        }
      });
    };

    if (!canViewSalesPriceList) {
      toRemoveColumns(removeColumn);
    }

    if (isIncomingPriceListTable) {
      // remove transfer stock column header
      toRemoveColumns(['TS QTY']);
    }

    if (isPoweredBuildsTable) {
      // Replace name column header label with Pc Bundle Name
      const replaceIndex = columnsWillAppear.findIndex(
        (i) => i.code === 'name'
      );

      columnsWillAppear.splice(replaceIndex, 1, {
        id: 1,
        label: 'PC BUNDLE NAME',
        minWidth: 170,
        code: 'name'
      });

      // Remove transfer stock column header
      if (!canViewSalesPriceList) {
        toRemoveColumns(removeColumn);
      }

      toRemoveColumns(['TS QTY']);
    }

    return columnsWillAppear;
  }, [
    isPoweredBuildsTable,
    selectedPriceListColumns,
    userPermissionsStringArray,
    canViewSalesPriceList,
    isIncomingPriceListTable
  ]);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    column: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const handleSortSelection = (direction: 'asc' | 'desc' | '') => {
    if (activeColumn) {
      onClickColumn({
        column: activeColumn,
        direction
      });
    }
    handleCloseMenu();
  };

  const onClickColumn = (payload: { column: string; direction: string }) => {
    const { column, direction } = payload;
    let newQueryParams = {
      ...params,
      ...(direction && { sort_by_column: `${column}_${direction}` })
    };

    if (!direction) {
      delete newQueryParams.sort_by_column;
    }

    const slug = objectToUrlSlug(newQueryParams);

    navigate({ search: String(slug) }, { replace: true });
  };

  const onProductClicked = (row: ProductPriceListData) => {
    // Do not show update dialog if in powered builds table
    if (!canUpdateRetailPrice || isPoweredBuildsTable) return;
    setSelectedProduct(row);
    setShowProductModal(true);
  };

  const onCloseProductModal = (action: string, data?: ProductPriceListData) => {
    setShowProductModal(false);
    if (action === 'update' && data) {
      onUpdatePriceList(data);
    }
  };

  //TODO: remove this solution later
  // const addUniqueBranch = useCallback(
  //   (product: Stock, branchNames: string[]) => {
  //     const { branch_name, stocks } = product;

  //     return !branch_name || !stocks || branchNames.includes(branch_name)
  //       ? branchNames
  //       : [...branchNames, branch_name];
  //   },
  //   []
  // );

  //TODO: remove this solution later
  // const pluckBranchesWithStock = useCallback(
  //   (product: ProductPriceListData) => {
  //     let uniqueBranches: string[] = [];

  //     [...(product.stocks ?? []), ...(product.transfer_stocks ?? [])].forEach(
  //       (stock) => {
  //         if (stock.branch_name) {
  //           uniqueBranches = addUniqueBranch(stock, uniqueBranches);
  //         }
  //       }
  //     );

  //     return uniqueBranches;
  //   },
  //   [addUniqueBranch]
  // );

  //TODO: remove this solution later
  // const extractAvailableBranchNames = (product: ProductPriceListData) => {
  //   const stockEntries = Object.values(product.stocks || {});
  //   const transferEntries = Object.values(product.transfer_stocks || {});
  //   const combined = [...stockEntries, ...transferEntries];

  //   const branchMap = new Map();

  //   combined.forEach(({ branch_id, branch_name, stocks }) => {
  //     if (stocks && stocks > 0 && !branchMap.has(branch_id)) {
  //       branchMap.set(branch_id, branch_name);
  //     }
  //   });

  //   return Array.from(branchMap.entries())
  //     .sort(([a], [b]) => a - b)
  //     .map(([, branch_name]) => branch_name);
  // };

  const extractAvailableBranch = (product: ProductPriceListData) => {
    const { stocks, transfer_stocks } = cloneDeep(product);
    const productStocks =
      stocks?.filter((stock) => stock.stocks && stock?.stocks > 0) || [];

    const product_transfer_stocks =
      transfer_stocks?.filter(
        (transfer_stock) => transfer_stock?.stocks && transfer_stock?.stocks > 0
      ) || [];

    const combined = [...productStocks, ...product_transfer_stocks];
    const uniqueByBranchId = uniqBy(combined, 'branch_id');
    const sortBranchName = sortBy(uniqueByBranchId, 'branch_id');
    //god I hate typescript
    const branchNames: string[] = sortBranchName
      .map((stock) => {
        if (stock?.branch_name && typeof stock?.branch_name === 'string') {
          return stock?.branch_name;
        }
        return '';
      })
      .filter((branchName) => branchName !== '');

    return branchNames || [];
  };

  const extractIncomingAvailableBranch = (
    product: IncomingProductPriceListData
  ) => {
    const { incoming_stocks } = cloneDeep(product);
    const incomingProductStocks =
      incoming_stocks?.filter(
        (stock) => stock.incoming_stocks && stock?.incoming_stocks > 0
      ) || [];

    const combined = [...incomingProductStocks];
    const uniqueByBranchId = uniqBy(combined, 'branch_id');
    const sortBranchName = sortBy(uniqueByBranchId, 'branch_id');
    //god I hate typescript
    const branchNames: string[] = sortBranchName
      .map((stock) => {
        if (stock?.branch_name && typeof stock?.branch_name === 'string') {
          return stock?.branch_name;
        }
        return '';
      })
      .filter((branchName) => branchName !== '');

    return branchNames || [];
  };

  return (
    <Fragment>
      <InfiniteScroll
        dataLength={priceList.length}
        next={onLoadMoreItems}
        hasMore={loadMore}
        loader={
          <p style={{ textAlign: 'center', marginTop: 12 }}>
            <b>Loading More...</b>
          </p>
        }
        endMessage={
          <p style={{ textAlign: 'center', marginTop: 12 }}>
            <b>
              {isLoading
                ? ''
                : priceList.length
                ? 'Yay! You have seen it all'
                : 'No Result Found!'}
            </b>
          </p>
        }
      >
        <LoaderBar isLoading={isLoading} />
        <TableContainer className={classes.table}>
          <Table>
            <TableHead>
              <TableRow>
                {internalColumnsWithPermissions?.map((columnItem, index) => (
                  <TableCell
                    key={index}
                    className={clsx(classes.tableCell, classes.tableHeader)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Only show button for sortable columns */}
                      {!nonSortableCodes.includes(columnItem.code) ? (
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, columnItem.code)}
                          style={{ cursor: 'pointer' }}
                        >
                          <ImportExportIcon
                            fontSize="inherit"
                            style={{ color: 'black' }}
                          />
                        </IconButton>
                      ) : null}

                      <TypographyPriceList text={columnItem.label} />
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {priceList.map((rowItem, index) => (
                <TableRow
                  key={index}
                  className={classes.tableRow}
                  onClick={() => onProductClicked(rowItem)}
                >
                  <Name
                    index={index}
                    data={rowItem}
                    branch_ids={branchIds}
                    isPoweredBuildsTable={isPoweredBuildsTable}
                    canViewSalesPriceList={canViewSalesPriceList}
                  />

                  <TableCell
                    className={clsx(classes.tableCell, classes.multiCellParent)}
                  >
                    <BranchStockList
                      branches={
                        isIncomingPriceListTable
                          ? extractIncomingAvailableBranch(rowItem)
                          : extractAvailableBranch(rowItem) || []
                      }
                      className={classes.boxCell}
                    />
                  </TableCell>
                  <TableCell
                    className={clsx(classes.tableCell, classes.multiCellParent)}
                  >
                    <StocksQuantityList
                      branches={
                        isIncomingPriceListTable
                          ? extractIncomingAvailableBranch(rowItem)
                          : extractAvailableBranch(rowItem)
                      }
                      className={classes.boxCell}
                      stocks={rowItem?.incoming_stocks || rowItem?.stocks || []}
                    />
                  </TableCell>
                  {isPoweredBuildsTable ||
                  isIncomingPriceListTable ||
                  !canViewSalesPriceList ? null : (
                    <TableCell
                      className={clsx(
                        classes.tableCell,
                        classes.multiCellParent
                      )}
                    >
                      <StocksQuantityList
                        branches={extractAvailableBranch(rowItem)}
                        className={classes.boxCell}
                        stocks={rowItem?.transfer_stocks || []}
                      />
                    </TableCell>
                  )}
                  <TableCell className={classes.tableCell}>
                    <TypographyPriceList
                      color={colors.grey[600]}
                      text={formatCurrency(
                        rowItem?.standard_price || 0,
                        'PHP '
                      )}
                      extraTextStyle={{ textDecoration: 'line-through' }}
                    />
                  </TableCell>

                  <TableCell
                    className={clsx(classes.tableCell, classes.multiCellParent)}
                  >
                    {(rowItem?.retail_price ?? 0) > 3000 ? (
                      <InstallmentList
                        installmentData={{
                          three_months:
                            rowItem?.installment?.three_months || '0',
                          six_months: rowItem?.installment?.six_months || '0',
                          twelve_months:
                            rowItem?.installment?.twelve_months || '0'
                        }}
                        className={classes.boxCell}
                      />
                    ) : (
                      <Typography
                        style={{ letterSpacing: 0.5, fontWeight: 600 }}
                      >
                        N/A
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <TypographyPriceList
                      text={formatCurrency(rowItem?.retail_price || 0, 'PHP ')}
                    />
                  </TableCell>
                  {canSeePriceListDealersPrice && canViewSalesPriceList && (
                    <TableCell className={classes.tableCell}>
                      <TypographyPriceList
                        color={colors.warning}
                        text={formatCurrency(
                          rowItem?.dealers_price || 0,
                          'PHP '
                        )}
                      />
                    </TableCell>
                  )}
                  {canSeePriceListGrossPrice && canViewSalesPriceList && (
                    <TableCell className={classes.tableCell}>
                      <TypographyPriceList
                        color={getColorGeneric(rowItem?.margin_status)}
                        text={formatCurrency(rowItem?.gross_price || 0, 'PHP ')}
                      />
                    </TableCell>
                  )}
                  {canSeePriceListMarginPercentage && canViewSalesPriceList && (
                    <TableCell className={classes.tableCell}>
                      <TypographyPriceList
                        color={getColorGeneric(rowItem?.margin_status)}
                        text={rowItem?.margin_percentage}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScroll>
      <ProductPriceModal
        visible={showProductModal && canViewSalesPriceList}
        product={selectedProduct || {}}
        onHandleClose={(action, data) =>
          isPoweredBuildsTable ? null : onCloseProductModal(action, data)
        }
      />

      {/* Sort Options Menu */}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleSortSelection('asc')}>
          <ArrowUpwardIcon fontSize="small" style={{ marginRight: 6 }} />
          Ascending
        </MenuItem>
        <MenuItem onClick={() => handleSortSelection('desc')}>
          <ArrowDownwardIcon fontSize="small" style={{ marginRight: 6 }} />
          Descending
        </MenuItem>
        <MenuItem onClick={() => handleSortSelection('')}>
          <ClearIcon fontSize="small" style={{ marginRight: 6 }} />
          Clear Sort
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

export const MainPriceListTable = memo(component);
